/**
 * Grouped theme.colors in client-config flatten to runtime CSS token keys.
 * Keep in sync with ui/scripts/lib/theme-colors-normalize.mjs
 */

export type FlatThemeColors = Record<string, string | undefined>;

export type ThemeColorsInput = Record<string, unknown>;

const GROUP_KEYS = ['brand', 'header', 'chat', 'sidebar', 'page', 'card', 'popover', 'roles'] as const;

const LEGACY_GROUP_ALIASES: Record<string, string> = {
  semantic: 'roles',
  shell: 'chat',
};
const DEPRECATED_GROUP_KEYS = ['wordmark', 'identity'] as const;

const ROLE_KEYS = [
  'secondary',
  'secondaryForeground',
  'accent',
  'accentForeground',
  'destructive',
  'destructiveForeground',
  'muted',
  'mutedForeground',
  'success',
  'successForeground',
  'warning',
  'warningForeground',
  'promotion',
  'promotionForeground',
  'info',
  'infoForeground',
  'border',
  'input',
  'skeleton',
] as const;

function resolveGroupedColorKeys(colors: ThemeColorsInput | undefined): ThemeColorsInput | undefined {
  if (!colors || typeof colors !== 'object') return colors;
  const out: ThemeColorsInput = structuredClone(colors);
  for (const key of DEPRECATED_GROUP_KEYS) delete out[key];
  for (const [legacy, canonical] of Object.entries(LEGACY_GROUP_ALIASES)) {
    const legacyGroup = out[legacy];
    if (legacyGroup && typeof legacyGroup === 'object') {
      const prev = out[canonical];
      out[canonical] = {
        ...(typeof prev === 'object' && prev ? (prev as object) : {}),
        ...(legacyGroup as object),
      };
    }
    delete out[legacy];
  }
  return out;
}

function liftHeaderBackground(out: ThemeColorsInput, headerColor: string | undefined) {
  if (!headerColor) return;
  const header = (typeof out.header === 'object' && out.header ? out.header : {}) as Record<
    string,
    string
  >;
  if (!header.background) header.background = headerColor;
  out.header = header;
}

function normalizeAuthoringGroups(colors: ThemeColorsInput | undefined): ThemeColorsInput | undefined {
  if (!colors || typeof colors !== 'object') return colors;
  const out: ThemeColorsInput = resolveGroupedColorKeys(colors) ?? colors;

  const actions = out.actions;
  if (actions && typeof actions === 'object') {
    const a = actions as Record<string, string>;
    const brand = (typeof out.brand === 'object' && out.brand ? out.brand : {}) as Record<string, string>;
    if (a.primary) brand.primary = a.primary;
    const onPrimary = a.onPrimary ?? a.primaryForeground;
    if (onPrimary) brand.onPrimary = onPrimary;
    out.brand = brand;
    delete out.actions;
  }

  const chrome = out.chrome;
  if (chrome && typeof chrome === 'object') {
    liftHeaderBackground(out, (chrome as Record<string, string>).header);
    delete out.chrome;
  }

  const surfaces = out.surfaces;
  if (surfaces && typeof surfaces === 'object') {
    const s = surfaces as Record<string, Record<string, string>>;
    for (const [legacyKey, canonical] of [
      ['shell', 'chat'],
      ['sidebar', 'sidebar'],
      ['page', 'page'],
    ] as const) {
      if (s[legacyKey]) {
        out[canonical] = {
          ...(typeof out[canonical] === 'object' && out[canonical] ? (out[canonical] as object) : {}),
          ...s[legacyKey],
        };
      }
    }
    delete out.surfaces;
  }

  const brand = out.brand;
  if (brand && typeof brand === 'object') {
    const b = brand as Record<string, string>;
    if (b.primaryForeground && !b.onPrimary) {
      b.onPrimary = b.primaryForeground;
      delete b.primaryForeground;
    }
    liftHeaderBackground(out, b.header ?? b.primaryDark);
    delete b.header;
    delete b.primaryDark;
    out.brand = b;
  }

  return out;
}

export function isGroupedThemeColors(colors: ThemeColorsInput | undefined): boolean {
  if (!colors || typeof colors !== 'object') return false;

  const normalized = normalizeAuthoringGroups(colors);
  if (GROUP_KEYS.some((k) => normalized?.[k] && typeof normalized[k] === 'object')) return true;
  if (
    ['actions', 'chrome', 'surfaces', 'shell'].some((k) => colors[k] && typeof colors[k] === 'object')
  ) {
    return true;
  }
  if (Object.keys(LEGACY_GROUP_ALIASES).some((k) => colors[k] && typeof colors[k] === 'object')) {
    return true;
  }
  if (DEPRECATED_GROUP_KEYS.some((k) => colors[k] && typeof colors[k] === 'object')) return true;

  const brand = colors.brand;
  if (brand && typeof brand === 'object') {
    const b = brand as Record<string, unknown>;
    if ('onPrimary' in b || 'header' in b || 'primaryForeground' in b || 'primaryDark' in b) {
      return true;
    }
    if (typeof b.primary === 'string') return true;
  }

  if (
    typeof colors.primary === 'string' ||
    typeof colors.sidebar === 'string' ||
    typeof colors.background === 'string' ||
    typeof colors.pageBackground === 'string'
  ) {
    return false;
  }

  return false;
}

export function mergeGroupedColors(
  base: ThemeColorsInput | undefined,
  delta: ThemeColorsInput | undefined,
): ThemeColorsInput | undefined {
  const resolvedBase = normalizeAuthoringGroups(base);
  const resolvedDelta = normalizeAuthoringGroups(delta);
  if (!resolvedDelta) return resolvedBase ? structuredClone(resolvedBase) : undefined;
  if (!resolvedBase) return structuredClone(resolvedDelta);
  const out: ThemeColorsInput = structuredClone(resolvedBase);
  for (const key of GROUP_KEYS) {
    const patch = resolvedDelta[key];
    if (!patch || typeof patch !== 'object') continue;
    const prev = out[key];
    out[key] = {
      ...(typeof prev === 'object' && prev ? (prev as object) : {}),
      ...(patch as object),
    };
  }

  return out;
}

function flattenGroupedRaw(colors: ThemeColorsInput): FlatThemeColors {
  const resolved = normalizeAuthoringGroups(colors) ?? colors;
  const flat: FlatThemeColors = {};

  const brand = (resolved.brand ?? {}) as Record<string, string>;
  if (brand.primary) flat.primary = brand.primary;
  const onPrimary = brand.onPrimary ?? brand.primaryForeground;
  if (onPrimary) flat.primaryForeground = onPrimary;

  const header = (resolved.header ?? {}) as Record<string, string>;
  if (header.background) flat.primaryDark = header.background;
  if (header.foreground) flat.primaryDarkForeground = header.foreground;

  const chat = (resolved.chat ?? {}) as Record<string, string>;
  if (chat.background) flat.background = chat.background;
  if (chat.foreground) flat.foreground = chat.foreground;

  const sidebar = (resolved.sidebar ?? {}) as Record<string, string>;
  if (sidebar.background) flat.sidebar = sidebar.background;
  if (sidebar.foreground) flat.sidebarForeground = sidebar.foreground;
  if (sidebar.accent) flat.sidebarAccent = sidebar.accent;
  if (sidebar.accentForeground) flat.sidebarAccentForeground = sidebar.accentForeground;

  const page = (resolved.page ?? {}) as Record<string, string>;
  if (page.background) flat.pageBackground = page.background;
  if (page.foreground) flat.pageForeground = page.foreground;

  const card = (resolved.card ?? {}) as Record<string, string>;
  if (card.background) flat.card = card.background;
  if (card.foreground) flat.cardForeground = card.foreground;

  const popover = (resolved.popover ?? {}) as Record<string, string>;
  if (popover.background) flat.popover = popover.background;
  if (popover.foreground) flat.popoverForeground = popover.foreground;

  const roles = (resolved.roles ?? {}) as Record<string, string>;
  for (const [key, value] of Object.entries(roles)) {
    if (typeof value === 'string' && value.trim()) flat[key] = value;
  }

  return flat;
}

export function applyThemeColorDerivations(flat: FlatThemeColors | undefined): FlatThemeColors | undefined {
  if (!flat) return flat;

  const out: FlatThemeColors = { ...flat };
  delete out.identity;

  if (!out.primaryDark && out.primary) {
    out.primaryDark = out.primary as string;
  }

  // Header text is not brand onPrimary: navy chrome stays light; same-as-primary headers follow onPrimary.
  if (!out.primaryDarkForeground) {
    if (
      out.primaryDark &&
      out.primary &&
      out.primaryDark !== out.primary
    ) {
      out.primaryDarkForeground = '#ffffff';
    } else if (out.primaryForeground) {
      out.primaryDarkForeground = out.primaryForeground as string;
    }
  }

  return out;
}

export function flattenThemeColors(
  colors: ThemeColorsInput | undefined,
  options: { baseColors?: ThemeColorsInput } = {},
): FlatThemeColors | undefined {
  if (!colors || typeof colors !== 'object') return undefined;
  if (!isGroupedThemeColors(colors)) {
    const flat: FlatThemeColors = {};
    for (const [key, value] of Object.entries(colors)) {
      if (typeof value === 'string' && value.trim()) flat[key] = value;
    }
    return applyThemeColorDerivations(flat);
  }

  const merged = options.baseColors
    ? mergeGroupedColors(options.baseColors, colors)
    : normalizeAuthoringGroups(colors);

  return applyThemeColorDerivations(flattenGroupedRaw(merged ?? colors));
}

export function resolveThemeModeFlat(
  theme: { colors?: ThemeColorsInput; dark?: { colors?: ThemeColorsInput } },
  mode: 'light' | 'dark',
): FlatThemeColors | undefined {
  if (!theme.colors) return undefined;
  if (mode === 'light') return flattenThemeColors(theme.colors);
  return flattenThemeColors(theme.dark?.colors ?? {}, { baseColors: theme.colors });
}

export function groupThemeColors(flat: FlatThemeColors | undefined): ThemeColorsInput | undefined {
  if (!flat || typeof flat !== 'object') return undefined;
  if (isGroupedThemeColors(flat)) return normalizeAuthoringGroups(flat);

  const normalized = applyThemeColorDerivations({ ...flat });
  if (!normalized) return undefined;

  const grouped: ThemeColorsInput = {};

  if (normalized.primary || normalized.primaryForeground) {
    grouped.brand = {
      ...(normalized.primary ? { primary: normalized.primary } : {}),
      ...(normalized.primaryForeground ? { onPrimary: normalized.primaryForeground } : {}),
    };
  }

  if (normalized.primaryDark && normalized.primaryDark !== normalized.primary) {
    grouped.header = {
      background: normalized.primaryDark,
      ...(normalized.primaryDarkForeground
        ? { foreground: normalized.primaryDarkForeground }
        : {}),
    };
  }

  if (normalized.background || normalized.foreground) {
    grouped.chat = {
      ...(normalized.background ? { background: normalized.background } : {}),
      ...(normalized.foreground ? { foreground: normalized.foreground } : {}),
    };
  }

  if (
    normalized.sidebar ||
    normalized.sidebarForeground ||
    normalized.sidebarAccent ||
    normalized.sidebarAccentForeground
  ) {
    grouped.sidebar = {
      ...(normalized.sidebar ? { background: normalized.sidebar } : {}),
      ...(normalized.sidebarForeground ? { foreground: normalized.sidebarForeground } : {}),
      ...(normalized.sidebarAccent ? { accent: normalized.sidebarAccent } : {}),
      ...(normalized.sidebarAccentForeground
        ? { accentForeground: normalized.sidebarAccentForeground }
        : {}),
    };
  }

  if (normalized.pageBackground || normalized.pageForeground) {
    grouped.page = {
      ...(normalized.pageBackground ? { background: normalized.pageBackground } : {}),
      ...(normalized.pageForeground ? { foreground: normalized.pageForeground } : {}),
    };
  }

  if (normalized.card || normalized.cardForeground) {
    grouped.card = {
      ...(normalized.card ? { background: normalized.card } : {}),
      ...(normalized.cardForeground ? { foreground: normalized.cardForeground } : {}),
    };
  }

  if (normalized.popover || normalized.popoverForeground) {
    grouped.popover = {
      ...(normalized.popover ? { background: normalized.popover } : {}),
      ...(normalized.popoverForeground ? { foreground: normalized.popoverForeground } : {}),
    };
  }

  const roles: Record<string, string> = {};
  for (const key of ROLE_KEYS) {
    const value = normalized[key];
    if (typeof value === 'string' && value.trim()) roles[key] = value;
  }
  if (Object.keys(roles).length) grouped.roles = roles;

  return grouped;
}
